import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { ChangeLog, Pagination, Response } from '@ui/src';
import { TransactionsTypesRiskScore } from '../../../types';
import { transactionsRiskApi } from '../services';

import ROUTES from '../../../../routes';

export interface TransactionsTypesRiskState {
  typesRisk?: TransactionsTypesRiskScore[];
  pagination?: Pagination;
  typeRisk?: TransactionsTypesRiskScore;
  changeLogs?: ChangeLog[];
  changeLogsPagination?: Pagination;
  errorsList?: string;
}

const initialState: TransactionsTypesRiskState = {};

export const transactionsTypesRiskFormSlice = createSlice({
  name: 'transactionsTypesRiskForm',
  initialState,
  reducers: {
    clearTypesRisk: (state) => {
      state.typesRisk = undefined;
      state.pagination = undefined;
      state.errorsList = undefined;
    },
    clearPanelState: (state) => {
      state.typeRisk = undefined;
      state.changeLogsPagination = undefined;
      state.changeLogs = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      transactionsRiskApi.endpoints.getTransactionTypesRisk.matchPending,
      (state) => {
        state.errorsList = undefined;
      },
    );
    builder.addMatcher(
      transactionsRiskApi.endpoints.getTransactionTypesRisk.matchFulfilled,
      (state, action: any) => {
        state.typesRisk = undefined;
        state.pagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.errorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.errorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      transactionsRiskApi.endpoints.getTransactionTypesRisk.matchFulfilled,
      (state, action: PayloadAction<Response<TransactionsTypesRiskScore>>) => {
        state.typesRisk = action?.payload?.data?.edges?.map((item) => ({
          ...item.node,
          url: generatePath(ROUTES.TRANSACTIONS_RISK_TYPES_RISK_EDIT, {
            typeId: item.node.id,
          }),
        }));

        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      transactionsRiskApi.endpoints.getTransactionTypeRisk.matchFulfilled,
      (state, action: PayloadAction<{ data: TransactionsTypesRiskScore }>) => {
        state.typeRisk = action?.payload?.data;
      },
    );
    builder.addMatcher(
      transactionsRiskApi.endpoints.getTransactionTypesRiskChangeLogs
        .matchFulfilled,
      (
        state,
        action: PayloadAction<Response<ChangeLog>> & {
          meta: { arg: { originalArgs: string } };
        },
      ) => {
        if (!action.meta.arg.originalArgs.includes('cursor')) {
          state.changeLogs = action.payload.data?.edges?.map(
            (item) => item.node,
          );
        } else {
          const nextChangeLogs = ([] as ChangeLog[]).concat(
            state.changeLogs as ChangeLog[],
          );

          action.payload.data.edges?.forEach((item) => {
            nextChangeLogs.push(item.node);
          });

          state.changeLogs = nextChangeLogs;
        }

        state.changeLogsPagination = action.payload.data?.pageInfo;
      },
    );
    builder.addMatcher(
      transactionsRiskApi.endpoints.updateTransactionTypesRisk.matchFulfilled,
      (state, action: PayloadAction<{ data: TransactionsTypesRiskScore }>) => {
        state.typesRisk = state.typesRisk?.map((item) => {
          const result = { ...item };

          if (result.id === action.payload.data.id) {
            result.riskScore = action.payload.data.riskScore;
            result.transactionTypeId = action.payload.data.transactionTypeId;
            result.transactionType = action.payload.data.transactionType;

            if (action.payload.data.paymentType) {
              result.paymentType = action.payload.data.paymentType;
            }
          }

          return result;
        });
      },
    );
  },
});

export const { clearPanelState, clearTypesRisk } =
  transactionsTypesRiskFormSlice.actions;

export default transactionsTypesRiskFormSlice.reducer;
